import {getReporterAssessmentScore, getReporterAssessmentRoles, normalizeReporterAssessmentConfig, getReporterAssessmentConfigErrors} from "./reporterAssessmentHelpers.js";
const cloneWorkData = value => JSON.parse(JSON.stringify(value || {}))
const isMissingValue = value => value === "" || value == null

export function createFixedPromptSnapshot(config = {}, previousForm = {}) {
	const form = cloneWorkData(config)
	for (const [currentKey, archivedKey] of [["fields", "archivedFields"], ["portraitSections", "archivedSections"]]) {
		const currentIds = new Set((form[currentKey] || []).map(item => item.id))
		const previous = [...(previousForm?.[currentKey] || []), ...(previousForm?.[archivedKey] || [])]
		form[archivedKey] = previous.filter(item => !currentIds.has(item.id)).map(item => ({...item, archived: true}))
	}
	if (form.subscaleProfile) {
		const currentIds = new Set((form.subscaleProfile.items || []).map(item => item.id))
		const previous = [...(previousForm?.subscaleProfile?.items || []), ...(previousForm?.subscaleProfile?.archivedItems || [])]
		form.subscaleProfile.archivedItems = previous.filter(item => !currentIds.has(item.id)).map(item => ({...item, archived: true}))
	}
	return form
}

export function validateReporterAssessmentPayload(enginePayload = {}, config = {}) {
	const assessment = normalizeReporterAssessmentConfig(config).reporterAssessment
	const selected = getReporterAssessmentRoles(config)
	if (!selected.length) throw new Error("Invalid reporter selection")
	const configErrors = getReporterAssessmentConfigErrors(config)
	if (configErrors.length) throw new Error(configErrors[0])
	const reports = enginePayload.reports || {}
	if (Object.keys(reports).some(reporter => !selected.includes(reporter))) throw new Error("Unexpected reporter form")
	if (!Object.keys(reports).length || !Object.values(reports).some(report => Object.keys(report.answers || {}).length)) throw new Error("No reporter answers")
	const scores = {}
	for (const reporter of selected) {
		const report = reports[reporter]
		if (!report) continue
		if (report.reporter !== reporter || !/^\d{4}-\d{2}-\d{2}$/.test(report.date || "")) throw new Error("Invalid reporter or administration date")
		const items = assessment.forms[reporter].items
		const ids = new Set(items.map(item => item.id))
		if (Object.keys(report.answers || {}).some(id => !ids.has(id))) throw new Error("Unexpected questionnaire item")
		const {invalidItems, ...score} = getReporterAssessmentScore(config, reporter, report.answers)
		if (invalidItems.length) throw new Error("Invalid questionnaire rating")
		scores[reporter] = score
	}
	delete enginePayload.privatePractitioner
	enginePayload.form = cloneWorkData(config)
	enginePayload.reporterScores = scores
	enginePayload.scoreResult = null
}

export function validateStructuredAssessmentPayload(enginePayload = {}, config = {}) {
	const assessment = config.assessment || {}
	const answers = enginePayload.answers || {}
	const fields = new Map((config.fields || []).map(field => [field.id, field]))
	const subtotals = {}
	if (isMissingValue(answers[assessment.dateFieldId])) throw new Error("Assessment date is missing")
	for (const domain of assessment.domains || []) {
		if (answers[domain.reviewedFieldId] !== "Reviewed") throw new Error("Checklist area not reviewed")
		for (const fieldId of [domain.currentFieldId, domain.pastFieldId, domain.principalFieldId].filter(Boolean)) {
			const selections = answers[fieldId] || []
			if (!Array.isArray(selections) || selections.some(value => !(fields.get(fieldId)?.answerOptions || []).includes(value))) {
				throw new Error("Invalid checklist selection")
			}
		}
	}
	const ratings = new Map()
	for (const item of assessment.items || []) {
		const rating = answers[item.fieldId]
		const ratingMax = item.anchors?.length ? item.anchors.length - 1 : assessment.ratingMax
		if (!Number.isInteger(rating) || rating < 0 || rating > ratingMax) throw new Error("Invalid clinician rating")
		ratings.set(item.fieldId, rating)
	}
	for (const item of assessment.ancillaryItems || []) {
		const rating = answers[item.fieldId]
		if (isMissingValue(rating)) continue
		if (!Number.isInteger(rating) || rating < 0 || rating >= item.anchors.length) throw new Error("Invalid supplementary rating")
	}
	for (const group of assessment.ratingGroups || []) {
		subtotals[group.id] = (group.itemIds || []).reduce((sum, fieldId) => sum + ratings.get(fieldId), 0)
	}
	const total = Object.values(subtotals).reduce((sum, value) => sum + value, 0)
	enginePayload.form = cloneWorkData(config)
	enginePayload.scoreResult = total
	enginePayload.assessmentScores = {subtotals, total, missingItems: [], missingDomains: []}
}

export function isStrictFormSubmission(config = {}) {
	return !!(config.validateFormAnswers || config.requireAnyTextAnswer || config.subscaleProfile || config.pairedQuestionnaire || config.frequencyInventory || config.assessment || config.reporterAssessment || config.handoutConfig)
}

export function validateFormWorkInput(payload = {}, config = {}) {
	const isRecord = value => value !== null && typeof value === "object" && !Array.isArray(value)
	if (!isRecord(payload)) throw new Error("Invalid form payload")
	if (payload.answers !== undefined && !isRecord(payload.answers)) throw new Error("Invalid form answers")
	if (config.pairedQuestionnaire || config.reporterAssessment) {
		if (payload.reports !== undefined && !isRecord(payload.reports)) throw new Error("Invalid form reports")
		for (const report of Object.values(payload.reports || {})) {
			if (!isRecord(report)) throw new Error("Invalid form report")
			if (report.answers !== undefined && !isRecord(report.answers)) throw new Error("Invalid report answers")
		}
	}
}
